/**
 * EmailAutocompleteInput — a plain-string email input with contact autocomplete.
 *
 * Unlike RecipientInput (chip-based, owns its value model), this wraps a normal
 * text input whose value stays a comma-separated string — so it drops into any
 * form that already stores addresses as a string (e.g. the Tech Support
 * composer's To/Cc/Bcc fields) without changing the surrounding state or
 * payload shape. Only the segment being typed (after the last comma) is
 * searched; picking a suggestion replaces just that segment.
 *
 * The search itself is injected via the `search` prop so the component works
 * against any recipients endpoint (portal or tech-support door).
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { settledAddresses, splitActiveSegment } from './emailSegments';
import styles from './EmailAutocompleteInput.module.css';

export interface EmailSuggestion {
  email: string;
  name: string | null;
  company: string | null;
  source?: string;
}

interface Props {
  /** The raw input value (may be a comma-separated address list). */
  value: string;
  onChange: (value: string) => void;
  /** Debounced contact search (called with the segment being typed). */
  search: (q: string) => Promise<EmailSuggestion[]>;
  placeholder?: string;
  /** Class applied to the <input> so it inherits the host form's styling. */
  className?: string;
  /** Allow comma-separated multi-address entry (default true). */
  multiple?: boolean;
  ariaLabel?: string;
}

const SOURCE_LABELS: Record<string, string> = {
  customer: 'customer',
  sales_account: 'account',
  ship_location: 'location',
  sales_rep: 'internal',
  email_history: 'email',
  sent_history: 'sent',
};

export default function EmailAutocompleteInput({
  value,
  onChange,
  search,
  placeholder,
  className,
  multiple = true,
  ariaLabel,
}: Props) {
  // Start dismissed: a PREFILLED value (reply-all Cc, an edited draft's
  // rehydrated Cc/Bcc, a derived To) must not fire a contact search — or pop
  // a dropdown over the form — before the user has typed anything. Typing
  // un-dismisses (handleInputChange).
  const [dismissed, setDismissed] = useState(true);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Only the segment being typed is searched; earlier segments are settled.
  const { prefix, activeSegment } = splitActiveSegment(value, multiple);

  // While dismissed there is nothing to show, so don't search either — three
  // prefilled fields would otherwise burn three recipient searches on every
  // composer open (the tech-support mailbox door is 429-prone).
  const { results, loading } = useDebouncedSearch(dismissed ? '' : activeSegment, search, {
    minLength: 2,
    debounceMs: 300,
  });

  // Hide addresses that are already in the settled part of the value.
  const suggestions = useMemo(() => {
    const settled = settledAddresses(prefix);
    return results.filter((r) => !settled.has(r.email.toLowerCase()));
  }, [results, prefix]);

  const isOpen = !dismissed && suggestions.length > 0;
  const showLoading =
    !dismissed && loading && activeSegment.length >= 2 && suggestions.length === 0;

  const close = useCallback(() => setDismissed(true), []);
  useClickOutside(wrapperRef, close);

  const handleSelect = useCallback(
    (s: EmailSuggestion) => {
      const settled = prefix ? `${prefix.replace(/[\s,;]+$/, '')}, ` : '';
      // The trailing ", " tees up the NEXT address (Gmail/Outlook convention)
      // so a second recipient autocompletes without the user having to know
      // to type a comma first. Hosts strip trailing separators on submit.
      onChange(`${settled}${s.email}${multiple ? ', ' : ''}`);
      setDismissed(true);
      setHighlightIndex(-1);
    },
    [prefix, multiple, onChange],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDismissed(false);
      setHighlightIndex(-1);
      onChange(e.target.value);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
          break;
        case 'Enter':
          if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
            e.preventDefault();
            handleSelect(suggestions[highlightIndex]);
          }
          break;
        case 'Escape':
          setDismissed(true);
          break;
      }
    },
    [isOpen, suggestions, highlightIndex, handleSelect],
  );

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <input
        className={className}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-label={ariaLabel}
      />

      {isOpen && (
        <div className={styles.dropdown} role="listbox">
          {suggestions.map((s, idx) => (
            <button
              key={`${s.email}-${idx}`}
              className={`${styles.option} ${idx === highlightIndex ? styles.optionHighlighted : ''}`}
              // Select on mousedown so the pick lands before the input blurs.
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(s);
              }}
              role="option"
              aria-selected={idx === highlightIndex}
              type="button"
              // Recorder: the label is a name and an email address, so the
              // row is named by family with the directory as its value
              // (kanban c2632a0b).
              data-action="recipient_pick"
              data-record-value="source"
              value={s.source}
            >
              <div className={styles.optionMain}>
                {s.name && <span className={styles.optionName}>{s.name}</span>}
                <span className={styles.optionEmail}>{s.email}</span>
              </div>
              {(s.company || s.source) && (
                <div className={styles.optionMeta}>
                  {s.company && <span className={styles.optionCompany}>{s.company}</span>}
                  {s.source && (
                    <span className={styles.sourceBadge}>
                      {SOURCE_LABELS[s.source] || s.source}
                    </span>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {showLoading && (
        <div className={styles.dropdown}>
          <div className={styles.loading}>Searching contacts...</div>
        </div>
      )}
    </div>
  );
}
